import { type VFC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { getItemCountString } from "../../app-utils/string.js";
import { type Configuration } from "../../graphql.js";
import { filterByLicense } from "../../redux/modules/config.js";
import {
  type IContentBrowserState,
  loadContentBrowserCounts,
  resetContentBrowserState,
} from "../../redux/modules/content-browser.js";
import { type SavedViewState } from "../../redux/modules/saved-view.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import {
  CONTENT_BROWSER_FEATURE_TYPE_ORGANISMS,
  GROUP_BY_ATTRIBUTE_FEATURES,
  GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE,
  GROUP_BY_ATTRIBUTE_SPECIES,
  GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP,
  GROUP_BY_TAXONOMIC_GROUP,
  LICENSE_MODULE,
} from "../../settings/settings.js";
import { ContextWidgetType, type INavigationContextProps } from "../core/navigation-panel/ContextWidget.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import ContentBrowserGenomesSubPage from "./ContentBrowserGenomesSubPage.js";

export const CONTENT_BROWSER_FEATURES: INavPanelCategory[] = [
  {
    label: "Genomes",
    tooltip: "Browse through the genomes",
    licenseModule: LICENSE_MODULE.EXPLORER,
    eventKey: CONTENT_BROWSER_FEATURE_TYPE_ORGANISMS,
    navItems: [
      {
        label: "Species",
        route: `organisms/${GROUP_BY_ATTRIBUTE_SPECIES}`,
        eventKey: GROUP_BY_ATTRIBUTE_SPECIES,
        default: true,
        pageComponent: <ContentBrowserGenomesSubPage />,
      },
      {
        label: "Genome Type",
        route: `organisms/${GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE}`,
        eventKey: GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE,
        pageComponent: <ContentBrowserGenomesSubPage />,
      },
      {
        label: "Feature Annotations",
        route: `organisms/${GROUP_BY_ATTRIBUTE_FEATURES}`,
        eventKey: GROUP_BY_ATTRIBUTE_FEATURES,
        pageComponent: <ContentBrowserGenomesSubPage />,
      },
      {
        label: "Genome Analysis Group",
        route: `organisms/${GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP}`,
        eventKey: GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP,
        pageComponent: <ContentBrowserGenomesSubPage />,
      },
      {
        label: "Taxonomy",
        route: `organisms/${GROUP_BY_TAXONOMIC_GROUP}`,
        eventKey: GROUP_BY_TAXONOMIC_GROUP,
        pageComponent: <ContentBrowserGenomesSubPage />,
      },
    ],
  },
];

/**
 * Content Browser main component
 */
const ContentBrowserPage: VFC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);
  const { content } = useSelector<IAppState, IContentBrowserState>((state) => state.contentBrowser);
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);

  useEffect(() => {
    dispatch(loadContentBrowserCounts());
    return () => {
      dispatch(resetContentBrowserState());
    };
  }, []);

  const getContextProps = (): INavigationContextProps => {
    if (viewId && !isSavedViewLoading && view && Number.parseInt(view?.id) === viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: view,
      };
    }
  };

  let navPanelCategories = filterByLicense(CONTENT_BROWSER_FEATURES, configuration);
  navPanelCategories = navPanelCategories.map((category) => ({
    ...category,
    badge: getItemCountString(content, category.eventKey),
  }));

  return <PageLayout contextWidgetProps={getContextProps()} navPanelCategories={navPanelCategories} />;
};

export default ContentBrowserPage;
